import { marked } from "marked";
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  LevelFormat,
  AlignmentType,
  convertInchesToTwip,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
  ExternalHyperlink,
  ShadingType,
} from "docx";

// ─── Border constants ────────────────────────────────────────────
const tableCellBorders = {
  top: { style: BorderStyle.SINGLE, size: 1, color: "dcdce0" },
  bottom: { style: BorderStyle.SINGLE, size: 1, color: "dcdce0" },
  left: { style: BorderStyle.SINGLE, size: 1, color: "dcdce0" },
  right: { style: BorderStyle.SINGLE, size: 1, color: "dcdce0" },
};

const headerCellBorders = {
  top: { style: BorderStyle.SINGLE, size: 1, color: "37474f" },
  bottom: { style: BorderStyle.SINGLE, size: 1, color: "37474f" },
  left: { style: BorderStyle.SINGLE, size: 1, color: "37474f" },
  right: { style: BorderStyle.SINGLE, size: 1, color: "37474f" },
};

const BLOCK_TAGS = new Set(["ul", "ol", "table", "pre", "blockquote"]);

// ─── Public API ──────────────────────────────────────────────────

/**
 * Converts a markdown string into a DOCX Blob ready for download.
 */
export async function buildDocxBlob(markdown: string): Promise<Blob> {
  const renderedHtml = marked.parse(markdown) as string;
  const docxElements = convertHtmlToDocxElements(renderedHtml);

  const doc = new Document({
    styles: {
      paragraphStyles: [
        {
          id: "Normal",
          name: "Normal",
          run: { size: 24, font: "Calibri" },
          paragraph: { spacing: { before: 0, after: 200, line: 276 } },
        },
        {
          id: "Heading1",
          name: "Heading 1",
          basedOn: "Normal",
          next: "Normal",
          run: { size: 32, bold: true, color: "1a1a1a" },
          paragraph: { spacing: { before: 240, after: 120 } },
        },
        {
          id: "Heading2",
          name: "Heading 2",
          basedOn: "Normal",
          next: "Normal",
          run: { size: 28, bold: true, color: "1a1a1a" },
          paragraph: { spacing: { before: 280, after: 100 } },
        },
        {
          id: "Heading3",
          name: "Heading 3",
          basedOn: "Normal",
          next: "Normal",
          run: { size: 26, bold: true, color: "1a1a1a" },
          paragraph: { spacing: { before: 200, after: 100 } },
        },
        {
          id: "Heading4",
          name: "Heading 4",
          basedOn: "Normal",
          next: "Normal",
          run: { size: 22, bold: true, color: "555555" },
          paragraph: { spacing: { before: 180, after: 80 } },
        },
        {
          id: "ListParagraph",
          name: "List Paragraph",
          basedOn: "Normal",
          run: {},
          paragraph: { indent: { left: convertInchesToTwip(0.25) } },
        },
      ],
    },
    numbering: {
      config: [
        {
          reference: "bullet-list",
          levels: [
            {
              level: 0,
              format: LevelFormat.BULLET,
              text: "\u2022",
              alignment: AlignmentType.LEFT,
              style: {
                paragraph: {
                  indent: {
                    left: convertInchesToTwip(0.5),
                    hanging: convertInchesToTwip(0.25),
                  },
                },
              },
            },
            {
              level: 1,
              format: LevelFormat.BULLET,
              text: "\u25CB",
              alignment: AlignmentType.LEFT,
              style: {
                paragraph: {
                  indent: {
                    left: convertInchesToTwip(1.0),
                    hanging: convertInchesToTwip(0.25),
                  },
                },
              },
            },
          ],
        },
        {
          reference: "number-list",
          levels: [
            {
              level: 0,
              format: LevelFormat.DECIMAL,
              text: "%1.",
              alignment: AlignmentType.LEFT,
              style: {
                paragraph: {
                  indent: {
                    left: convertInchesToTwip(0.5),
                    hanging: convertInchesToTwip(0.25),
                  },
                },
              },
            },
            {
              level: 1,
              format: LevelFormat.DECIMAL,
              text: "%2.",
              alignment: AlignmentType.LEFT,
              style: {
                paragraph: {
                  indent: {
                    left: convertInchesToTwip(1.0),
                    hanging: convertInchesToTwip(0.25),
                  },
                },
              },
            },
          ],
        },
      ],
    },
    sections: [
      {
        properties: {
          page: {
            margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 },
          },
        },
        children: docxElements,
      },
    ],
  });

  return Packer.toBlob(doc);
}

// ─── HTML → DOCX element conversion ─────────────────────────────

function convertHtmlToDocxElements(htmlString: string): (Paragraph | Table)[] {
  const parser = new DOMParser();
  const htmlDoc = parser.parseFromString(htmlString, "text/html");
  const elements: (Paragraph | Table)[] = [];

  htmlDoc.body.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE && !node.textContent?.trim()) return;

    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      const tag = element.tagName.toLowerCase();

      if (tag === "h1") {
        elements.push(new Paragraph({
          text: element.textContent || "",
          heading: HeadingLevel.HEADING_1,
          style: "Heading1",
        }));
      } else if (tag === "h2") {
        elements.push(new Paragraph({
          text: element.textContent || "",
          heading: HeadingLevel.HEADING_2,
          style: "Heading2",
        }));
      } else if (tag === "h3") {
        elements.push(new Paragraph({
          text: element.textContent || "",
          heading: HeadingLevel.HEADING_3,
          style: "Heading3",
        }));
      } else if (tag === "h4" || tag === "h5" || tag === "h6") {
        elements.push(new Paragraph({
          text: element.textContent || "",
          heading: HeadingLevel.HEADING_4,
          style: "Heading4",
        }));
      } else if (tag === "p") {
        elements.push(new Paragraph({
          children: parseInlineElements(element),
          style: "Normal",
        }));
      } else if (tag === "ul") {
        parseListItems(element, "bullet-list", elements);
      } else if (tag === "ol") {
        parseListItems(element, "number-list", elements);
      } else if (tag === "table") {
        elements.push(buildTable(element));
      } else if (tag === "pre") {
        elements.push(buildCodeBlock(element));
      } else if (tag === "blockquote") {
        buildBlockquote(element).forEach((p) => elements.push(p));
      } else if (tag === "hr") {
        elements.push(new Paragraph({
          children: [],
          border: {
            bottom: { style: BorderStyle.SINGLE, size: 1, color: "e0e0e0", space: 1 },
          },
          spacing: { before: 300, after: 300 },
        }));
      } else {
        elements.push(new Paragraph({
          children: parseInlineElements(element),
          style: "Normal",
        }));
      }

    } else if (node.nodeType === Node.TEXT_NODE) {
      const textContent = node.textContent || "";
      if (textContent.trim()) {
        elements.push(new Paragraph({ text: textContent.trim(), style: "Normal" }));
      }
    }
  });

  return elements;
}

// ─── List items (handles nesting) ────────────────────────────────

function parseListItems(
  listElement: HTMLElement,
  reference: string,
  elements: (Paragraph | Table)[],
  level: number = 0
) {
  for (let i = 0; i < listElement.children.length; i++) {
    const li = listElement.children[i] as HTMLElement;
    if (li.tagName.toLowerCase() !== "li") continue;

    const inlineRuns = parseInlineElements(li);

    if (inlineRuns.length > 0) {
      elements.push(new Paragraph({
        children: inlineRuns,
        style: "ListParagraph",
        numbering: { reference, level },
        spacing: { before: 60, after: 60 },
      }));
    }

    li.querySelectorAll(":scope > ul").forEach((nestedUl) => {
      parseListItems(nestedUl as HTMLElement, "bullet-list", elements, Math.min(level + 1, 1));
    });
    li.querySelectorAll(":scope > ol").forEach((nestedOl) => {
      parseListItems(nestedOl as HTMLElement, "number-list", elements, Math.min(level + 1, 1));
    });
  }
}

// ─── Table builder ───────────────────────────────────────────────

function buildTable(tableElement: HTMLElement): Table {
  const rows: TableRow[] = [];
  let rowIndex = 0;

  tableElement.querySelectorAll("tr").forEach((tr) => {
    const cells: TableCell[] = [];
    const isHeader = tr.querySelector("th") !== null;

    tr.querySelectorAll("th, td").forEach((cell) => {
      cells.push(
        new TableCell({
          children: [
            new Paragraph({
              children: isHeader
                ? [new TextRun({
                    text: cell.textContent || "",
                    bold: true,
                    color: "ffffff",
                    font: "Calibri",
                    size: 21,
                  })]
                : parseInlineElements(cell as HTMLElement),
              spacing: { before: 40, after: 40 },
            }),
          ],
          shading: isHeader
            ? { fill: "37474f", type: ShadingType.CLEAR, color: "auto" }
            : rowIndex % 2 === 1
              ? { fill: "f8f8fa", type: ShadingType.CLEAR, color: "auto" }
              : undefined,
          borders: isHeader ? headerCellBorders : tableCellBorders,
        })
      );
    });

    if (cells.length > 0) {
      rows.push(new TableRow({ children: cells, tableHeader: isHeader }));
      if (!isHeader) rowIndex++;
    }
  });

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows,
  });
}

// ─── Code block builder ─────────────────────────────────────────

function buildCodeBlock(preElement: HTMLElement): Paragraph {
  const codeText = preElement.textContent || "";
  const lines = codeText.split("\n");
  if (lines.length > 0 && lines[lines.length - 1].trim() === "") {
    lines.pop();
  }

  const runs: TextRun[] = [];
  lines.forEach((line, i) => {
    if (i > 0) {
      runs.push(new TextRun({ break: 1, font: "Consolas", size: 20 }));
    }
    runs.push(new TextRun({
      text: line || " ",
      font: "Consolas",
      size: 20,
      color: "2d2d2d",
    }));
  });

  return new Paragraph({
    children: runs,
    shading: { fill: "f7f7fa", type: ShadingType.CLEAR, color: "auto" },
    spacing: { before: 160, after: 160, line: 300 },
    border: {
      top: { style: BorderStyle.SINGLE, size: 1, color: "e5e5ea" },
      bottom: { style: BorderStyle.SINGLE, size: 1, color: "e5e5ea" },
      left: { style: BorderStyle.SINGLE, size: 1, color: "e5e5ea" },
      right: { style: BorderStyle.SINGLE, size: 1, color: "e5e5ea" },
    },
  });
}

// ─── Blockquote builder ─────────────────────────────────────────

function buildBlockquote(bqElement: HTMLElement): Paragraph[] {
  const paragraphs: Paragraph[] = [];
  const bqStyle = {
    indent: { left: convertInchesToTwip(0.35) },
    border: {
      left: { style: BorderStyle.SINGLE, size: 18, color: "b0b0b8", space: 8 },
    },
    shading: { fill: "fafafa", type: ShadingType.CLEAR, color: "auto" } as const,
  };

  const childElements = bqElement.querySelectorAll(":scope > *");
  if (childElements.length > 0) {
    childElements.forEach((child) => {
      const runs = parseInlineElements(child as HTMLElement);
      paragraphs.push(new Paragraph({
        children: runs.length > 0 ? runs : [new TextRun({ text: child.textContent || "", color: "4a4a4a" })],
        spacing: { before: 60, after: 60 },
        ...bqStyle,
      }));
    });
  } else {
    paragraphs.push(new Paragraph({
      children: [new TextRun({ text: bqElement.textContent || "", color: "4a4a4a" })],
      spacing: { before: 60, after: 60 },
      ...bqStyle,
    }));
  }

  return paragraphs;
}

// ─── Inline element parser ──────────────────────────────────────

function parseInlineElements(domNode: HTMLElement): (TextRun | ExternalHyperlink)[] {
  const children: (TextRun | ExternalHyperlink)[] = [];

  domNode.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      const t = child.textContent || "";
      if (!t.trim()) return;

      const parts = t.split("\n");
      for (let idx = 0; idx < parts.length; idx++) {
        if (idx > 0) {
          children.push(new TextRun({ break: 1 }));
        }
        if (parts[idx]) {
          children.push(new TextRun(parts[idx]));
        }
      }
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      const element = child as HTMLElement;
      const tag = element.tagName.toLowerCase();

      if (BLOCK_TAGS.has(tag)) return;

      if (tag === "br") {
        children.push(new TextRun({ break: 1 }));
        return;
      }

      if (tag === "a") {
        const href = element.getAttribute("href") || "";
        children.push(new ExternalHyperlink({
          children: [
            new TextRun({
              text: element.textContent || href,
              color: "1a5276",
              underline: {},
              style: "Hyperlink",
            }),
          ],
          link: href,
        }));

      } else if (tag === "code") {
        children.push(new TextRun({
          text: element.textContent || "",
          font: "Consolas",
          size: 21,
          color: "3d3d3d",
          shading: { fill: "f4f4f7", type: ShadingType.CLEAR, color: "auto" },
        }));

      } else {
        let formatting: {
          bold?: boolean;
          italics?: boolean;
        } = {};

        if (tag === "strong" || tag === "b") formatting.bold = true;
        if (tag === "em" || tag === "i") formatting.italics = true;

        if (element.children.length > 0) {
          element.childNodes.forEach((nested) => {
            if (nested.nodeType === Node.TEXT_NODE) {
              const nt = nested.textContent;
              if (nt && nt.trim()) {
                children.push(new TextRun({ text: nt, ...formatting }));
              }
            } else if (nested.nodeType === Node.ELEMENT_NODE) {
              const ne = nested as HTMLElement;
              const neTag = ne.tagName.toLowerCase();
              const nestedFormat = { ...formatting };
              if (neTag === "strong" || neTag === "b") nestedFormat.bold = true;
              if (neTag === "em" || neTag === "i") nestedFormat.italics = true;
              if (neTag === "code") {
                children.push(new TextRun({
                  text: ne.textContent || "",
                  font: "Consolas",
                  size: 21,
                  color: "3d3d3d",
                  shading: { fill: "f4f4f7", type: ShadingType.CLEAR, color: "auto" },
                  ...nestedFormat,
                }));
              } else if (neTag === "a") {
                const href = ne.getAttribute("href") || "";
                children.push(new ExternalHyperlink({
                  children: [
                    new TextRun({
                      text: ne.textContent || href,
                      color: "1a5276",
                      underline: {},
                      ...nestedFormat,
                    }),
                  ],
                  link: href,
                }));
              } else if (ne.textContent && ne.textContent.trim()) {
                children.push(new TextRun({ text: ne.textContent, ...nestedFormat }));
              }
            }
          });
        } else if (element.textContent && element.textContent.trim()) {
          children.push(new TextRun({ text: element.textContent, ...formatting }));
        }
      }
    }
  });

  return children;
}
